import { Card, Col, FormLabel, Row, Spinner } from "react-bootstrap";
import BitacoraFiltros from "./componentes/filters";
import { useEffect, useState, useCallback } from "react";
import {
  MasterTableBitacora,
  BitacoraTableListTanstack,
} from "./componentes/bitacora-lista/TablaBitacoras";
import { BitacoraService } from "../../../services/apiService";
import { toast } from "react-toastify";
import PrintReportModule from "../../../components/print/PrintReportModule";
import { AccionBitacora, guardarBitacora } from "../../../utils/bitacora";
import {
  LoadingEmptyState,
  WelcomeEmptyState,
  NoDataEmptyState,
  APIError
} from '../../../components/common/progressemptystate.js';
import StandardProgressBar from '../../../components/common/ProgressBar/StandardProgressBar';

const BitacoraReport = () => {
  const [isLoading, setIsLoading] = useState(false); // Estado para controlar el spinner
  const [error, setError] = useState(null);
  const [searchInitiated, setSearchInitiated] = useState(false);

  const [filters, setFilters] = useState({
    fecha_inicio: new Date(new Date().setHours(0, 0, 0, 0)).toISOString(),
    fecha_fin: new Date(new Date().setHours(23, 59, 59, 999)).toISOString(),
    uids_usuario: [],
  });

  const [loadingComponents, setLoadingComponents] = useState({
    table: { loading: false, progress: 0, complete: false },
    vehicles: { loading: false, progress: 0, complete: false }
  });

  const calculateTotalProgress = useCallback(() => {
    const weights = {
      table: 0.4,      // ✅ Ajustar para que sume 100%
      vehicles: 0.3,   // ✅ Solo componentes que existen
      alerts: 0.3,     // ✅ Solo componentes que existen                
    };

    let totalProgress = 0;
    let totalWeight = 0;

    Object.entries(loadingComponents).forEach(([component, state]) => {
      if (state && (state.loading || state.complete)) {
        const progress = Number(state.progress);
        if (!isNaN(progress)) {
          totalProgress += progress * weights[component];
          totalWeight += weights[component];
        } else {
          totalProgress += (state.complete ? 100 : 0) * weights[component];
          totalWeight += weights[component];
        }
      }
    });

    if (totalWeight === 0) return 0;
    const result = totalProgress / totalWeight;

    return Math.min(Math.max(result, 0), 100);
  }, [loadingComponents]);

  const [debugMetrics, setDebugMetrics] = useState({
    searchStartTime: null,
    searchEndTime: null,

    connections: {
      'ListTable': {
        name: 'Bitacora (GPS)',
        startTime: null,
        endTime: null,
        totalRecordsExpected: 0,
        totalRecordsReceived: 0,
        totalApiCalls: 0,
        completedApiCalls: 0,
        failedApiCalls: 0,
        recoveredApiCalls: 0,
        apiCallsHistory: [],
        downloadProgress: 0,
        status: 'pending'
      }
    },

    globalTotalRecordsExpected: 0,
    globalTotalRecordsReceived: 0,
    globalDownloadProgress: 0,
    activeConnections: 0,

    componentTimes: {
      table: { start: null, end: null },
      vehicles: { start: null, end: null },
      alerts: { start: null, end: null },
      map: { start: null, end: null },
      frequency: { start: null, end: null }
    }
  });

  const updateComponentProgress = useCallback((component, progress, isComplete = false) => {
    setLoadingComponents(prevState => {
      const newState = { ...prevState };

      if (newState[component]) {
        newState[component] = {
          ...newState[component],
          loading: !isComplete,
          progress: isComplete ? 100 : Math.max(0, Math.min(100, progress)),
          complete: isComplete
        };
      }

      return newState;
    });

    // ✅ Actualizar métricas directamente sin dependencia circular
    setDebugMetrics(prev => ({
      ...prev,
      componentTimes: {
        ...prev.componentTimes,
        [component]: {
          start: !isComplete ? Date.now() : (prev.componentTimes[component]?.start || Date.now()),
          end: isComplete ? Date.now() : null
        }
      }
    }));
  }, []);

  const updateDebugMetrics = useCallback((updates) => {
    setDebugMetrics(prev => {
      if (!prev || !prev.connections) {
        console.warn('⚠️ debugMetrics no está inicializado correctamente');
        return prev || {
          connections: {},
          globalTotalRecordsExpected: 0,
          globalTotalRecordsReceived: 0,
          globalDownloadProgress: 0,
          activeConnections: 0,
          componentTimes: {}
        };
      }
      const newState = { ...prev };

      if (updates.connectionId && newState.connections[updates.connectionId]) {
        const connection = newState.connections[updates.connectionId];

        if (!connection.apiCallsHistory) {
          connection.apiCallsHistory = [];
        }

        // Actualizar datos específicos de la conexión
        Object.keys(updates).forEach(key => {
          if (key !== 'connectionId') {
            if (key === 'apiCallsHistory' && Array.isArray(updates[key])) {
              const newCalls = updates[key].map(call => ({
                ...call,
                timestamp: call.timestamp || Date.now()
              }));
              connection[key] = [...(connection[key] || []), ...newCalls];

              if (connection[key].length > 50) {
                connection[key] = connection[key].slice(-50);
              }
            } else if (key !== 'apiCallsHistory') {
              connection[key] = updates[key];
            }
          }
        });

        const activeConnections = Object.values(newState.connections).filter(conn =>
          conn && (conn.status === 'downloading' || conn.status === 'completed')
        );

        newState.globalTotalRecordsExpected = activeConnections.reduce((sum, conn) =>
          sum + (conn.totalRecordsExpected || 0), 0
        );

        newState.globalTotalRecordsReceived = activeConnections.reduce((sum, conn) =>
          sum + (conn.totalRecordsReceived || 0), 0
        );

        newState.globalDownloadProgress = newState.globalTotalRecordsExpected > 0 ?
          (newState.globalTotalRecordsReceived / newState.globalTotalRecordsExpected * 100) : 0;

        newState.activeConnections = activeConnections.length;

      } else if (updates.connectionId && !newState.connections[updates.connectionId]) {
        newState.connections[updates.connectionId] = {
          name: updates.connectionId,
          startTime: null,
          endTime: null,
          totalRecordsExpected: 0,
          totalRecordsReceived: 0,
          totalApiCalls: 0,
          completedApiCalls: 0,
          failedApiCalls: 0,
          recoveredApiCalls: 0,
          apiCallsHistory: [],
          downloadProgress: 0,
          status: 'pending'
        };

        Object.keys(updates).forEach(key => {
          if (key !== 'connectionId') {
            newState.connections[updates.connectionId][key] = updates[key];
          }
        });

      } else {
        Object.keys(updates).forEach(key => {
          if (key === 'componentTimes') {
            if (!newState.componentTimes) {
              newState.componentTimes = {};
            }
            newState.componentTimes = { ...newState.componentTimes, ...updates.componentTimes };
          } else if (key !== 'connectionId') {
            newState[key] = updates[key];
          }
        });
      }

      return newState;
    });

    // ✅ SINCRONIZAR DESCARGA CON BARRA DE PROGRESO
    if (updates.connectionId && updates.downloadProgress !== undefined) {
      setTimeout(() => {
        const componentMapping = {
          'ListTable': 'list'
        };

        const componentName = componentMapping[updates.connectionId];

        if (componentName) {
          // ✅ Usar el progreso real de descarga para actualizar el componente
          const isComplete = updates.downloadProgress >= 100;
          const progressValue = Math.round(updates.downloadProgress || 0);

          console.log(`🔄 [Sincronización] ${componentName}: ${progressValue}% (descarga ${isComplete ? 'completa' : 'en progreso'})`);

          updateComponentProgress(componentName, progressValue, isComplete);

          // ✅ Si la descarga está completa, también marcar como cargando = false
          if (isComplete) {
            setLoadingComponents(prevState => ({
              ...prevState,
              [componentName]: {
                loading: false,
                progress: 100,
                complete: true
              }
            }));
          }
        }
      }, 0);
    }
  }, [updateComponentProgress]);

  useEffect(() => {
    guardarBitacora({
      categoria: "Reportes Bitácora",
      observacion: "Bitácora",
      tipoAccion: AccionBitacora.EntrarReporte,
    });
  }, []);
  const handleGenerateReport = (selectedFilters) => {
    setFilters(selectedFilters);
    setSearchInitiated(true); // ✅ Marcar que se inició búsqueda
    setError(null); // ✅ Limpiar errores
    actualizarBitacoras([]);
  };
  const [bitacoras, actualizarBitacoras] = useState([]);

  useEffect(() => {
    //Metodo para Listar todas las bitacoras del reporte
    const loadBitacoras = async (filtros) => {
      if (!filtros.uids_usuario || filtros.uids_usuario.length === 0) {
        return;
      }

      setIsLoading(true);
      setError(null);

      try {

        // ✅ ESTABLECER TIEMPO DE INICIO SI NO ESTÁ YA ESTABLECIDO
        setDebugMetrics(prev => ({
          ...prev,
          searchStartTime: prev.searchStartTime || Date.now(),
          searchEndTime: null
        }));

        // ✅ MARCAR COMPONENTE COMO CARGANDO AL INICIAR DESCARGA
        setLoadingComponents(prevState => ({
          ...prevState,
          table: { loading: true, progress: 0, complete: false }
        }));

        updateComponentProgress('table', 10, false);

        const response = await BitacoraService.getBitacoras(filtros);
        if (response && response.statusCode === 200) {

          updateComponentProgress('table', 30, false);

          const data = response.data
            .map((grupo) => ({
              fecha: grupo.fecha,
              usuario: `${grupo.usuario_nombre}`,
              observaciones: grupo.observacion,
              categoria: grupo.categoria,
            }))
            // ✅ ORDENAR POR FECHA (MÁS VIEJO AL MÁS NUEVO)
            .sort((a, b) => {
              // Convertir fechas ISO a objetos Date para comparar
              const fechaA = new Date(a.fecha);
              const fechaB = new Date(b.fecha);
              return fechaA - fechaB; // Ascendente (viejo → nuevo)
            });

          // ✅ Progreso intermedio
          updateComponentProgress('table', 60, false);

          actualizarBitacoras(data);

          // ✅ Progreso plugin renderizado de tabla
          updateComponentProgress('table', 80, false);
          updateComponentProgress('table', 90, false);

        } else {
          //toast.error(response?.message || "Error al generar reporte");
          setError(response?.message || "Error al generar reporte");
        }
      } catch (error) {
        console.error("Error al cargar bitácoras:", error);
        //toast.error("Error al cargar bitácoras");
        setError("Error al cargar bitácoras. Por favor, intente nuevamente.");
      } finally {
        setIsLoading(false); // Desactivar el spinner después de la petición       
        updateComponentProgress('table', 100, true); // ✅ Completar tabla
      }
    };
    loadBitacoras(filters);
  }, [filters]);

  useEffect(() => {
    if (!window.printReportModule) {
      window.printReportModule = new PrintReportModule({
        captureQuality: 0.95,
        pdfFormat: "A4",
        pdfOrientation: "portrait",
        useNativeTables: true,
        maxRowsPerPage: 30,
        tableFontSize: 7,

        // ✅ CONFIGURACIÓN ESPECÍFICA PARA BiTACORA
        reportType: "Bitacoras",
        onlyTableExport: true,

        tableExtractionConfig: {
          enabled: true,
          selector: "#tabla-bitacoras",
          title: "Listado Bitácora",
          type: "auto",
        },
        // horizontalStrategy: 'rows',
        // horizontalRows: [
        //   {
        //     title: 'Listado de Bitácoras',
        //     selector: '#tabla-bitacoras',
        //     identifier: 'bitacoras-tabla',
        //     selectorMethod: 'direct'
        //   }
        // ],

        // verticalStrategy: 'targets',
        // captureTargets: [
        //   {
        //     title: 'Listado de Bitácoras',
        //     selector: '#tabla-bitacoras',
        //     type: 'table'
        //   }
        // ]
      });
    }
    return () => {
      if (window.printReportModule) {
        delete window.printReportModule;
      }
    };
  }, []);

  // Estructura de columnas
  const columns = [
    {
      accessorKey: "observaciones",
      header: "Observaciones",
      cell: ({ row: { original } }) => original.observaciones,
    },
    {
      accessorKey: "usuario",
      header: "Nombre Usuario",
      cell: ({ row: { original } }) => original.usuario,
    },
    {
      accessorKey: "fecha",
      header: "Fecha",
      cell: ({ row: { original } }) =>
        original.fecha /*formatFecha(original.fecha)*/,
    },
  ];
  const PER_PAGE = 100;
  const [searchValue, setSearchValue] = useState("");
  const [perPage, setPerPage] = useState(PER_PAGE);
  const handlePageSizeChange = useCallback((newSize) => {
    setPerPage(newSize);
  }, []);


  const renderContent = () => {
    // 1. Estado inicial - no se han seleccionado usuarios
    if (!searchInitiated || filters.uids_usuario.length === 0) {
      return <WelcomeEmptyState reportType="bitácora" />;
    }

    // 2. Estado de carga
    if (isLoading) {
      return <LoadingEmptyState reportType="bitácora" />;
    }

    // 3. Estado de error
    if (error) {
      return (
        <APIError
          message={error}
          onRetry={() => {
            setError(null);
            const currentFilters = { ...filters };
            setFilters({ ...currentFilters });
          }}
          onClose={() => setError(null)}
        />
      );
    }

    // 4. Sin datos encontrados
    if (bitacoras.length === 0) {
      return <NoDataEmptyState reportType="bitácora" />;
    }

    // 5. Mostrar contenido con datos
    return (
      <div id="reporte-bitacoras-content" data-report-area="bitacoras">
        <Row className="mt-3">
          <Col md={12} xs={12}>
            <div id="tabla-bitacoras">
              <BitacoraTableListTanstack
                data={bitacoras}
                columns={columns}
                searchValue={searchValue}
                onSearch={setSearchValue}
                pageSize={perPage}
                onPageSizeChange={handlePageSizeChange}
                isLoading={false}
                title="Listado Bitácora"
                onTableRendered={(count) => {
                  console.log("🔔 Tabla renderizada con", count, "registros");
                }}
              />
            </div>
          </Col>
        </Row>
      </div>
    );
  };


  return (
    <div>
      <div className=" m-3 ">
        <Row>
          <Col md={10}>
            <h4 className="mt-2">Bitácora</h4>
          </Col>
        </Row>
      </div>

      <Card className="p-3 mb-3 mt-3">
        {/* Titulo principal de la pagina */}
        {/* <div className="d-flex justify-content-between align-items-center mb-1 mt-3">
                    <h4 className="mb-0">Bitácora</h4>
                </div> */}
        <hr className="text-danger" />

        <BitacoraFiltros
          onGenerateReport={handleGenerateReport}
          imprimir={
            filters.uids_usuario.length == 0
              ? false
              : isLoading
                ? false
                : bitacoras.length > 0
                  ? true
                  : false
          }
        />

        <Row>
          <Col md={12}>
            <StandardProgressBar
              calculateTotalProgress={calculateTotalProgress}
              debugMetrics={debugMetrics}
            />
          </Col>
        </Row>
      </Card>

      {renderContent()}

    </div>
  );
};

export default BitacoraReport;
